// Considered: MessageAtom — rejected. It extends PrimeReact's MessageProps, so its `content`
// node can technically render the proposal's Keep (primary) / Discard pair; but fully overriding
// `content` is the only way to get them, and an atom whose entire body is replaced contributes
// nothing but a border and a severity colour this bar does not use. See spec Reuse Audit
// (`GhostBarMolecule`).
//
// The prototype's `t-ghost-bar` (`type-composer.jsx:694-699`, `:1351-1356`, `:740-745`): one
// sparkle, one sentence summarising what the agent proposed for THIS section, one primary Keep.
// Discard is this repo's addition — a proposal a user cannot decline is a defect
// (`/standards/agentic/agentic-ux-standards.md` Rule 4), and the prototype's mock has no reject
// path because nothing in it ever writes.

import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { ComposerGhostProvenance } from '../../../composer/agentTransport';
import './GhostBarMolecule.scss';

export interface GhostBarMoleculeProps {
  /** The one-sentence summary of what this section's proposal changes. */
  message: string;
  /** Contract datum the bar itself carries (kind `ghost-bar`), e.g. `arcs.renewal-proposed-as-a-second-arc`. */
  messageDatum?: string;
  /** Label of the primary action — "Keep it" / "Keep them", chosen by the caller from the subject count. */
  keepLabel: string;
  /** Contract datum the Keep button carries, e.g. `arcs.keep-it`. */
  keepDatum?: string;
  /**
   * Where the proposal came from (Agentic UX Rule 7). Absent only for a ghost with no thread turn
   * behind it — a seeded story or a proposal restored from a previous session — in which case the
   * bar states nothing rather than inventing an origin.
   */
  provenance?: ComposerGhostProvenance;
  /** Sends the user to the turn named by `provenance.turnId`. Omitted when the thread is not mounted. */
  onShowOrigin?: (turnId: string) => void;
  onKeep: () => void;
  onDiscard: () => void;
  /** The Keep write is in flight — disables both actions so a second click cannot issue a second write. */
  keeping?: boolean;
  'data-testid'?: string;
}

/**
 * The turn's clock time in the APP's locale, not the browser's. A ghost is reviewed in the session
 * that produced it, so the time of day is the useful half of the instant; an unparseable stamp
 * renders as the raw string rather than `Invalid Date`.
 *
 * `locale` is threaded rather than left `undefined`, which resolves to the browser's language: an
 * app running fr-FR in an en-US browser would otherwise set `10:32 AM` inside a French sentence.
 */
function formatProvenanceTime(at: string, locale: string): string {
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return at;
  return when.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

const GhostBarMolecule: React.FC<GhostBarMoleculeProps> = ({
  message,
  messageDatum,
  keepLabel,
  keepDatum,
  onKeep,
  onDiscard,
  provenance,
  onShowOrigin,
  keeping = false,
  'data-testid': testId = 'composer-ghost-bar',
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="composer-ghost-bar"
      data-testid={testId}
      data-datum={messageDatum}
      data-kind={messageDatum ? 'ghost-bar' : undefined}
      role="status"
    >
      <i className="pi pi-sparkles composer-ghost-bar__mark" aria-hidden="true" />
      <div className="composer-ghost-bar__body">
        <span className="composer-ghost-bar__message">{message}</span>
        {provenance && (
          <span className="composer-ghost-bar__provenance" data-testid={`${testId}-provenance`}>
            {t('studio.typeEditor.composer.ghost.provenance', {
              prompt: provenance.prompt,
              at: formatProvenanceTime(provenance.at, i18n.language),
            })}
            {onShowOrigin && (
              <ButtonAtom
                variant="text"
                size="small"
                className="composer-ghost-bar__origin"
                onClick={() => onShowOrigin(provenance.turnId)}
                data-testid={`${testId}-show-origin`}
              >
                {t('studio.typeEditor.composer.ghost.showOrigin')}
              </ButtonAtom>
            )}
          </span>
        )}
      </div>
      <div className="composer-ghost-bar__actions">
        <ButtonAtom
          variant="text"
          size="small"
          onClick={onDiscard}
          disabled={keeping}
          data-testid={`${testId}-discard`}
        >
          {t('studio.typeEditor.composer.ghost.discard')}
        </ButtonAtom>
        <ButtonAtom
          variant="primary"
          size="small"
          onClick={onKeep}
          disabled={keeping}
          data-testid={`${testId}-keep`}
          data-datum={keepDatum}
          data-kind={keepDatum ? 'button' : undefined}
        >
          {keepLabel}
        </ButtonAtom>
      </div>
    </div>
  );
};

GhostBarMolecule.displayName = 'GhostBarMolecule';

export default GhostBarMolecule;
